import { motion } from "framer-motion";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Logo } from "../components/Logo";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Lock, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (password.length < 8) { toast.error("La contraseña debe tener al menos 8 caracteres"); return; }
    if (password !== confirm) { toast.error("Las contraseñas no coinciden"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      if (data.session_token) localStorage.setItem("session_token", data.session_token);
      setUser(data.user);
      setDone(true);
      toast.success("Contraseña actualizada");
      setTimeout(() => window.location.replace("/dashboard"), 1400);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No se pudo restablecer la contraseña.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
        <div className="flex justify-center mb-8"><Logo size={44} /></div>
        <div className="bg-white rounded-3xl p-8 shadow-[0_8px_30px_rgb(61,64,91,0.06)] border border-stone-100">
          {!token ? (
            <div className="text-center" data-testid="reset-invalid">
              <AlertTriangle size={32} className="mx-auto text-amber-500 mb-3" />
              <h2 className="font-outfit text-xl font-bold text-brand-ink">Enlace inválido</h2>
              <p className="text-sm text-brand-ink/70 mt-2">Falta el token del enlace. Solicita uno nuevo desde "Olvidé mi contraseña".</p>
              <button onClick={() => navigate("/login")} data-testid="reset-goto-login"
                className="mt-5 text-brand-terracotta font-outfit font-semibold hover:underline">Ir a iniciar sesión</button>
            </div>
          ) : done ? (
            <div className="text-center" data-testid="reset-done">
              <CheckCircle2 size={32} className="mx-auto text-brand-sage mb-3" />
              <h2 className="font-outfit text-xl font-bold text-brand-ink">¡Listo!</h2>
              <p className="text-sm text-brand-ink/70 mt-2">Tu contraseña se actualizó. Entrando a tu panel…</p>
            </div>
          ) : (
            <>
              <h2 className="font-outfit text-2xl font-bold text-brand-ink">Nueva contraseña</h2>
              <p className="text-muted-foreground mt-2 font-nunito text-sm">Crea una contraseña segura de al menos 8 caracteres.</p>
              <form onSubmit={handleSubmit} data-testid="reset-form" className="mt-6 space-y-3">
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-ink/40" />
                  <input data-testid="reset-password-input" type="password" required value={password}
                    onChange={(e) => setPassword(e.target.value)} placeholder="Nueva contraseña"
                    className="w-full pl-9 pr-3 py-3 rounded-2xl border border-stone-200 bg-stone-50 text-sm font-nunito focus:outline-none focus:ring-2 focus:ring-brand-terracotta/40" />
                </div>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-ink/40" />
                  <input data-testid="reset-confirm-input" type="password" required value={confirm}
                    onChange={(e) => setConfirm(e.target.value)} placeholder="Repite la contraseña"
                    className="w-full pl-9 pr-3 py-3 rounded-2xl border border-stone-200 bg-stone-50 text-sm font-nunito focus:outline-none focus:ring-2 focus:ring-brand-terracotta/40" />
                </div>
                <button data-testid="reset-submit-btn" type="submit" disabled={submitting}
                  className="w-full flex items-center justify-center gap-2 bg-brand-ink hover:bg-brand-ink/90 text-white font-outfit font-semibold py-3.5 rounded-2xl transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-60 disabled:hover:scale-100">
                  {submitting && <Loader2 size={18} className="animate-spin" />}
                  Guardar contraseña
                </button>
              </form>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}
